/**
 * Video upload and run-status API client (plan 16.2's two-phase upload flow; 7.6's
 * status/process routes, already live per this phase's brief).
 *
 * A new module, not an addition to services/api.js (P4 set the precedent with
 * standardsApi.js: a phase's own routes get their own file). Imports the shared axios
 * instance from './api' so every request still carries the bearer-token and 401
 * interceptors already wired up there.
 */
import api from './api';

// Named timeout constants (main app convention, plan 16.4's closing bullet): a multi-GB
// video upload cannot share the rest of the client's default request timeout, and
// prepare-upload is a tiny JSON POST that should fail fast instead of hanging for 45
// minutes if the network is down.
const PREPARE_UPLOAD_TIMEOUT_MS = 30 * 1000;
const UPLOAD_FILE_TIMEOUT_MS = 45 * 60 * 1000;
// The four direct-upload calls are small JSON round trips (never the file bytes — those go
// straight from the browser to S3 via the presigned part URLs, plan 2026-09-26), so they share
// prepare-upload's fast-fail timeout rather than upload-file's 45-minute one.
const DIRECT_UPLOAD_TIMEOUT_MS = 30 * 1000;
// Finding J, 2026-09-26: `complete` is the one direct-upload call whose OWN work (S3's
// CompleteMultipartUpload) can genuinely run long on a many-thousand-part object, and
// CloudFront gives its origin 60s to answer before ITS OWN timeout fires -- longer than the
// 30s above. A 30s client timeout could therefore abandon a `complete` call CloudFront was
// still going to let finish, showing the user a false failure for an upload the server was
// about to complete successfully. 120s comfortably covers CloudFront's 60s with room to
// spare for the S3 call itself.
const DIRECT_UPLOAD_COMPLETE_TIMEOUT_MS = 120 * 1000;

export const videosAPI = {
  // Step 1 of the two-phase flow (plan 16.2): creates the `videos` row (status UPLOADING)
  // and returns it so the caller has an id to upload the file against.
  prepareUpload: (projectId, body, options = {}) =>
    api.post(`/api/v1/projects/${projectId}/videos/prepare-upload`, body, {
      timeout: PREPARE_UPLOAD_TIMEOUT_MS,
      ...options,
    }),

  // Step 2: multipart upload of the actual bytes. `onUploadProgress` is axios's own hook,
  // wired straight through so the caller can drive a real percentage bar (plan 16's
  // "Change 4: byte-level progress" — the main app's own upload has no equivalent).
  // `options` accepts `{ signal }` so a caller can cancel an in-flight upload.
  uploadFileForVideo: (videoId, formData, onUploadProgress, options = {}) =>
    api.post(`/api/v1/videos/${videoId}/upload-file`, formData, {
      timeout: UPLOAD_FILE_TIMEOUT_MS,
      onUploadProgress,
      ...options,
    }),

  // Current (or most recent) pipeline run for one video — the only place `stage` lives;
  // ProjectVideoSummary (GET /projects/{id}/videos) does not carry it.
  getStatus: (videoId) => api.get(`/api/v1/videos/${videoId}/status`),

  // D21: uploading never starts processing — this is the auditor's separate "Start"
  // action. `force` (default false) lets a caller re-run a done/failed video (7.7's
  // state machine); omitted here since nothing in this phase needs it yet.
  process: (videoId, body = {}) => api.post(`/api/v1/videos/${videoId}/process`, body),

  // Permanent: the row, its runs, findings and report, and its stored files (uploads.py's
  // DELETE). The Videos list offers it so an unfinished upload or photo set can be cleared.
  remove: (videoId) => api.delete(`/api/v1/videos/${videoId}`),

  // Browser-to-S3 direct multipart upload (Anshul, 2026-09-26): a large file (>=
  // config.upload.direct_upload_min_bytes) skips uploadFileForVideo entirely — the browser
  // PUTs part bytes straight to S3 via the presigned URLs `getDirectUploadPartUrls` returns,
  // never routing gigabytes through this backend. All four calls are tiny JSON bodies; the
  // actual bytes go out over `window.fetch`, not this shared axios instance (its bearer-token
  // interceptor would add an `Authorization` header S3 rejects on a presigned URL).
  startDirectUpload: (videoId, body, options = {}) =>
    api.post(`/api/v1/videos/${videoId}/direct-upload/start`, body, {
      timeout: DIRECT_UPLOAD_TIMEOUT_MS,
      ...options,
    }),

  // Signed in batches, just in time (a presigned URL dies with the credentials that signed
  // it) — the caller asks for only the next few part numbers it is about to send.
  getDirectUploadPartUrls: (videoId, body, options = {}) =>
    api.post(`/api/v1/videos/${videoId}/direct-upload/parts`, body, {
      timeout: DIRECT_UPLOAD_TIMEOUT_MS,
      ...options,
    }),

  completeDirectUpload: (videoId, body, options = {}) =>
    api.post(`/api/v1/videos/${videoId}/direct-upload/complete`, body, {
      timeout: DIRECT_UPLOAD_COMPLETE_TIMEOUT_MS,
      ...options,
    }),

  // No body: the video id and its own stored upload_id/key are enough for the server to
  // abort the in-progress multipart upload.
  abortDirectUpload: (videoId, options = {}) =>
    api.post(`/api/v1/videos/${videoId}/direct-upload/abort`, null, {
      timeout: DIRECT_UPLOAD_TIMEOUT_MS,
      ...options,
    }),
};

export default videosAPI;
